import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  QueryOperator,
  ExactMatchQueryOperators,
  EXACT_MATCH_QUERY_OPERATORS,
  VqlQuery,
  Filters,
} from "types";

import QueryModalOperators from "views/components/QueryModal/QueryModalOperators";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { getSelectedQueryModal } from "redux/SingleDataset/selectors";
import {
  createAndUpdateIssueTypesFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import {
  setSelectedQueryModal,
  updateSelectedQueryModal,
} from "redux/SingleDataset/actions";
import RangeInput from "views/components/QueryModal/RangeInput";
import QueryModal from "views/components/QueryModal";
import OutliersIcon from "assets/icons/OutliersIcon";
import useVqlParams from "hooks/useVqlParams";
import styles from "./style.module.scss";

interface OutliersQueryModalProps {
  editQueryId?: string;
}

export default function OutliersQueryModal({
  editQueryId,
}: OutliersQueryModalProps) {
  const { findVqlQuery, addVqlQuery, updateVqlQuery, vqlQueries } =
    useVqlParams();
  const selectedModalData = useSelector((state) =>
    getSelectedQueryModal(state),
  );

  const issueQuery = editQueryId ? findVqlQuery(editQueryId, "issues") : null;
  const oldQuery = issueQuery?.issues.value === "outliers" ? issueQuery : null;

  const queryOperator = oldQuery
    ? convertAPIOperator(oldQuery.issues.mode)
    : QueryOperator.IS;

  const range: [number, number] = oldQuery
    ? [oldQuery.issues.confidence_min, oldQuery.issues.confidence_max]
    : [0.8, 1]; //mock data

  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const existingQuery = useMemo(() => {
    if (editQueryId) {
      return null;
    }
    return (
      vqlQueries.find(
        (query: VqlQuery) =>
          isVqlQuery(query, "issues") &&
          query.issues.value === "outliers" &&
          convertAPIOperator(query.issues?.mode) === queryOperator,
      ) || null
    );
  }, [vqlQueries, queryOperator, editQueryId]);

  const submitQuery = (
    operator: ExactMatchQueryOperators,
    newRange: [number, number],
  ) => {
    const queryId = editQueryId || existingQuery?.id;
    const vqlQuery = createAndUpdateIssueTypesFilter(
      "outliers",
      operator,
      newRange,
      queryId,
    );
    queryId ? updateVqlQuery(vqlQuery) : addVqlQuery(vqlQuery);
    if (selectedModalData) {
      dispatch(
        setSelectedQueryModal({ ...selectedModalData, id: vqlQuery.id }),
      );
    }
  };

  const handleOptionSelection = (operator: QueryOperator) => {
    if (operator === queryOperator) return;

    submitQuery(operator as ExactMatchQueryOperators, range);
  };

  const handleSubmission = (value: number[]) => {
    if (value[0] === range[0] && value[1] === range[1]) return;

    submitQuery(queryOperator as ExactMatchQueryOperators, [
      value[0],
      value[1],
    ]);
  };

  useEffect(() => {
    const query = vqlQueries.find(
      (query: VqlQuery) =>
        isVqlQuery(query, "issues") &&
        query.issues?.value === "outliers" &&
        convertAPIOperator(query.issues?.mode) === queryOperator,
    );
    if (query) {
      dispatch(
        updateSelectedQueryModal({ type: Filters.OUTLIERS, id: query.id }),
      );
    } else {
      submitQuery(queryOperator as ExactMatchQueryOperators, range);
      dispatch(updateSelectedQueryModal({ type: Filters.OUTLIERS }));
    }
    // eslint-disable-next-line
  }, [queryOperator]);

  return (
    <QueryModal modalRef={modalRef} keepOpenOnLoading>
      <QueryModalTitle
        icon={<OutliersIcon />}
        info="Flags images that look very different from the rest of the dataset and don’t fit the typical pattern."
      >
        Outliers
      </QueryModalTitle>
      <div className={styles.filterContent}>
        <QueryModalOperators
          operators={[...EXACT_MATCH_QUERY_OPERATORS]}
          selectedOperator={queryOperator}
          onSelect={handleOptionSelection}
        />
        <RangeInput
          range={range}
          title="Confidence:"
          onChange={handleSubmission}
        />
      </div>
    </QueryModal>
  );
}
